import { airportIanaZone } from '@/lib/disruptions/airport-tz';

/**
 * Airport-local wall time ↔ UTC instant, for the declaration form (U1).
 *
 * The operator types STD / ETD / ATD in the DEPARTURE airport's local time — the time
 * on the departures board — never their browser's zone (the bug class `airport-tz.ts`
 * exists for). The wire wants an instant WITH an offset (cases rejects an offsetless
 * one), so the form converts `YYYY-MM-DDTHH:mm` (a `datetime-local` value) in the
 * airport's IANA zone to `…Z`. DST-correct via `Intl` — no extra dependency.
 *
 * No origin known → UTC, labelled as such by the form (never a silent guess).
 */

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/** The zone the form works in: the airport's IANA zone, or UTC without an airport. */
export function zoneFor(originIata: string | null | undefined): string {
  return originIata ? airportIanaZone(originIata) : 'UTC';
}

/** Offset of `zone` at instant `ms`, in minutes (local − UTC). */
function offsetMinutes(ms: number, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60_000);
}

/**
 * `2026-06-03T23:25` read in `zone` → `2026-06-04T02:25:00.000Z`. Returns `null` for a
 * value that is not a complete `datetime-local`. A wall time skipped by a DST jump
 * resolves to the instant just after the jump (the usual convention).
 */
export function localToUtcIso(local: string, zone: string): string | null {
  const m = LOCAL_RE.exec(local.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number) as [number, number, number, number, number, number];
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  // Two passes: the offset at the guessed instant, then at the corrected one (DST edges).
  let ms = wall - offsetMinutes(wall, zone) * 60_000;
  ms = wall - offsetMinutes(ms, zone) * 60_000;
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** An instant → the `datetime-local` value it reads as in `zone` (prefill). `''` when unparseable. */
export function utcIsoToLocal(iso: string | null | undefined, zone: string): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const local = new Date(ms + offsetMinutes(ms, zone) * 60_000);
  return local.toISOString().slice(0, 16);
}
